import {
  Fragment,
  ReactNode,
  useState,
} from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { useTranslation } from 'react-i18next';

import { SkeletonLoader } from 'libs/design-system/src/components/SkeletonLoader';

import {
  PaperPlane,
  Plus,
} from '@unified-checkout/design-system/icons/index';

import type { IButton } from 'libs/design-system/src/types/Button/button';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/bills';
import { BILLS } from '@app/dashboard/constants/Bills';

import type { IHeader } from '@app/dashboard/types/innerLayout/bills/header';

import { BalancesDetails } from '../../../../../components/inner_layout/BalanceDetails';
import { TransferModal } from '../components/Modals/Transfer';
import { AddFundsModal } from '../components/Modals/AddFunds';
import { isDistributor } from '../utils';

import styles from './header.module.scss';

export const Header = ({
  savedMerchants,
  billsData,
  isBillsLoading
}: IHeader) => {
  const { t } = useTranslation();

  const [showTransfer, setShowTransfer] = useState(false);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  const billsBalance = billsData?.data?.data?.balance;
  const hasSavedMerchants = !!savedMerchants.length;

  const {
    amount,
    title,
    currency,
    tooltip,
  } = billsBalance || {};

  const handleOpenTransfer = () => {
    setShowTransfer(true);

    amplitude.track(BILLS_EVENTS.BILLS_TRANSFER_INITIATED, {
      [BILLS_EVENTS_PARAMS.HAS_SAVED_MERCHANTS]: !!hasSavedMerchants,
      [BILLS_EVENTS_PARAMS.BILLS_BALANCE]: amount
    });
  };
  const handleOpenAddFunds = () => {
    setShowAddFunds(true);
    setActiveTab(BILLS);

    amplitude.track(BILLS_EVENTS.BILLS_ADD_FUNDS_SELECTED, {
      [BILLS_EVENTS_PARAMS.BILLS_BALANCE]: amount
    });
  }

  const btnProps: IButton[] = [
    ...(isDistributor() ?
      [{
        text: t('innerLayout.billsPage.transferButton'),
        trailingIcon: PaperPlane,
        onClickHandler: handleOpenTransfer,
      }]
      :
      []),
    {
      text: t('innerLayout.billsPage.addFundsButton'),
      leadingIcon: Plus,
      onClickHandler: handleOpenAddFunds,
    },
  ];

  const modalsProps = {
    transfer: {
      showTransfer,
      setShowTransfer,
      merchants: savedMerchants,
      data: billsBalance,
    },
    addFunds: {
      cardData: billsBalance,
      showAddFunds,
      setShowAddFunds,
      activeTab,
      setActiveTab,
    },
  };
  const cardSkeletonLoader = ({
    section,
    height,
    noOfRows,
    key,
    isLoading
  }: {
    section: ReactNode;
    height: string;
    noOfRows: number;
    key: string;
    isLoading: boolean;
  }): ReactNode => {
    return isLoading ? (
      <SkeletonLoader
        type="bordered_box"
        noOfRows={noOfRows}
        height={height}
        key={key}
      />
    ) : (
      section && <Fragment key={key}>{section}</Fragment>
    );
  };

  return (
    <div className={styles['header']}>
      <div className={styles['header__title']}>
        {t('innerLayout.navbar.bills')}
      </div>
      {cardSkeletonLoader({
        section:
          <BalancesDetails
            title={title ?? t('innerLayout.billsPage.billsBalance')}
            hintText={tooltip?.sub_title}
            amount={amount}
            currency={currency}
            btnProps={btnProps}
          />,
        height: '8rem',
        noOfRows: 2,
        key: 'pending-balance',
        isLoading: isBillsLoading
      })}

      <TransferModal {...modalsProps.transfer} />
      <AddFundsModal {...modalsProps.addFunds} />
    </div>
  );
};
